/* Click and Collect order list, Vue Component */
<template>
  <v-card id="click-collect-list">
    <v-card-title>
      Click &amp; Collect
      <v-spacer></v-spacer>
      <v-btn color="primary" :disabled="loading" @click="downloadReport">
        <v-icon class="me-1" size="18">{{ icons.mdiDownload }}</v-icon>
        Driver Report
      </v-btn>
    </v-card-title>

    <v-card-text class="pb-4">

      <!-- BCR-119 AB-05: the sub-status filter the daily driver report is taken from -->
      <v-row>
        <v-col cols="12">
          <v-radio-group v-model="collectionFilter" row hide-details class="mt-0 pt-0"
            @change="searchData">
            <v-radio label="All" value="All"></v-radio>
            <v-radio label="Ready to Collect" value="Ready"></v-radio>
            <v-radio label="Awaiting Collection" value="Awaiting"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>

      <div class="cc-filters d-flex flex-wrap align-center mt-3">
        <v-text-field class="cc-filters__field" v-model="search.all" single-line dense outlined
          hide-details placeholder="Search Fields" @keyup.enter="searchData"></v-text-field>
        <v-text-field class="cc-filters__field" v-model="search.Reference" single-line dense outlined
          hide-details placeholder="Reference" @keyup.enter="searchData"></v-text-field>
        <v-text-field class="cc-filters__field" v-model="search.MemberId_Reference" single-line dense
          outlined hide-details placeholder="Customer ID" @keyup.enter="searchData"></v-text-field>
        <v-text-field class="cc-filters__field" v-model="search.MemberId_FullName" single-line dense
          outlined hide-details placeholder="Customer Name" @keyup.enter="searchData"></v-text-field>
        <v-btn class="cc-filters__button" color="primary" @click="searchData">
          Search
        </v-btn>
      </div>
    </v-card-text>

    <!-- table -->
    <v-data-table :headers="tableColumns" :items="items" :options.sync="options" :server-items-length="totalItems"
      :loading="loading" :footer-props="footerProps" class="text-no-wrap">

      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center">
          <!-- AB-06: straight into the standard book-in screen -->
          <v-btn v-if="item.CollectionStatus === 'Ready to Collect'" small color="success"
            :to="{ name: 'order-bookitem', params: { id: item.Id } }">
            Collect
          </v-btn>
        </div>
      </template>

      <template #[`item.Reference`]="{ item }">
        <router-link class="font-weight-medium text-decoration-none"
          :to="{ name: 'order-view', params: { id: item.Id } }">
          {{ item.Reference }}
        </router-link>
      </template>

      <template #[`item.MemberId_Reference`]="{ item }">
        <router-link class="font-weight-medium text-decoration-none"
          :to="{ name: 'member-view', params: { id: item.MemberId } }">
          {{ item.MemberId_Reference }}
        </router-link>
      </template>

      <template #[`item.CollectionStatus`]="{ item }">
        <click-collect-status-chip :status="item.CollectionStatus"></click-collect-status-chip>
      </template>

      <template #[`item.CollectionDate`]="{ item }">
        {{ Display.DisplayDate(item.CollectionDate) }}
      </template>

      <template #[`item.Created`]="{ item }">
        {{ Display.DateTime(item.Created) }}
      </template>

      <template #[`item.DeliveryAddressId_Summary`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="text-overflow-ellipses" style="width: 180px;">{{
              item.DeliveryAddressId_Summary }}</div>
          </template>
          <span>{{ item.DeliveryAddressId_Summary }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>

import store from '@/store'
import { saveAs } from 'file-saver'

import { mdiDownload } from '@mdi/js'

import { ref, watch, computed, getCurrentInstance } from '@vue/composition-api'
import * as Display from '@/datahelpers/displayTypes'
import ClickCollectStatusChip from '@/views/order/ClickCollectStatusChip'

export default {
  components: {
    ClickCollectStatusChip
  },
  setup() {

    const vm = getCurrentInstance().proxy;

    // define cols - AB-01: the details the ops team needs to identify the order at the retailer
    const tableColumns = [
      { text: 'Reference', value: 'Reference' },
      {
        text: '',
        value: 'actions',
        align: 'center',
        sortable: false,
      },
      { text: 'Collection Status', value: 'CollectionStatus', sortable: false },
      { text: 'Collection Date', value: 'CollectionDate' },
      { text: 'Retailer Reference', value: 'RetailerReference' },
      { text: 'Retailer', value: 'ClickCollectRetailerId_Name' },
      { text: 'Customer ID', value: 'MemberId_Reference' },
      { text: 'Customer', value: 'MemberId_FullName' },
      { text: 'Delivery', value: 'DeliveryAddressId_Summary' },
      { text: 'Booked', value: 'Created' },
    ];

    // three params for fetching. Only orders still awaiting collection belong here - once the
    // admin books one in it moves to Held/Ready and drops off the tab by itself. The bookable
    // statuses (Active, Pending) are exactly that set, and they are resolved at fetch time
    // because the status lookup is loaded asynchronously into the store.
    const bookableStatuses = computed(() => store.getters["app/OrderStatusAllowBook"]);
    const search = ref({});
    const collectionFilter = ref('All');
    const filter = ref({ ClickCollect: true });

    // AB-04: collections that are due sort to the top without the admin filtering for them
    const options = ref({
      sortBy: ['CollectionDate'],
      sortDesc: [false],
      itemsPerPage: 20
    });
    const footerProps = {
      'items-per-page-options': [10, 20, 50, 100, -1]
    };

    const items = computed(() => store.state.app.Orders || []);
    const totalItems = computed(() => store.state.app.OrdersTotal || 0);
    const loading = ref(false);

    const applyFilters = () => {
      if (bookableStatuses.value && bookableStatuses.value.length > 0) {
        filter.value.StatusIds = bookableStatuses.value.map(x => parseInt(x));
      }

      if (collectionFilter.value === 'Ready') {
        filter.value.ReadyToCollect = true;
      } else if (collectionFilter.value === 'Awaiting') {
        filter.value.ReadyToCollect = false;
      } else {
        delete filter.value.ReadyToCollect;
      }
    }

    // method for data loading via vuex
    const fetchData = () => {
      applyFilters();
      store
        .dispatch('app/fetchOrders', {
          search: search.value,
          options: options.value,
          filter: filter.value,
        })
        .then(response => {
          loading.value = false
        })
        .catch(error => {
          console.log(error)
        })
    }

    watch([options], () => {
      loading.value = true
      fetchData()
    }, { deep: true });

    function searchData() {
      this.$set(this.options, 'page', 1);
      loading.value = true
      fetchData()
    }

    // AB-05: the printable/exportable report the driver takes to the retailer. Built server-side so it
    // covers every matching order, not just the page currently on screen. The API returns a real
    // workbook so the reference columns stay text in Excel, hence the blob response type.
    const downloadReport = () => {
      loading.value = true
      applyFilters();
      vm.$http
        .post(`${store.state.apiUrl}/api/orders/click-collect/xlsx`, {
          search: search.value,
          filter: filter.value,
          options: options.value
        }, { responseType: 'blob' })
        .then(response => {
          saveAs(response.data, `click-and-collect-${new Date().toLocaleDateString()}.xlsx`);
        })
        .catch(ex => {
          console.error(ex);
        })
        .finally(() => {
          loading.value = false
        })
    }

    return {
      tableColumns,
      search,
      filter,
      collectionFilter,
      options,
      totalItems,
      items,
      loading,
      footerProps,
      Display,
      icons: {
        mdiDownload
      },
      downloadReport,
      searchData
    }
  }
}
</script>

<style lang="scss" scoped>
// The filter row wraps as the card narrows: each field flexes to share the space and drops to
// the next line when it runs out, while the button keeps its natural width so it always stays
// inside the card rather than overflowing the edge.
.cc-filters {
  gap: 8px;

  &__field {
    flex: 1 1 180px;
    min-width: 150px;
  }

  &__button {
    flex: 0 0 auto;
  }
}
</style>
